import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, Building2, HelpCircle, LayoutDashboard, LogOut, Menu, Megaphone, PlusCircle } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { AvatarPill } from "@/components/shared/AvatarPill";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { SkipLink } from "@/components/a11y/SkipLink";
import { MobileNav, MobileNavCloseButton } from "@/components/layout/MobileNav";

export function HqLayout() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  const navItems = [
    { to: "/hq/dashboard",      icon: LayoutDashboard, label: "Overview" },
    { to: "/hq/communications", icon: Megaphone,        label: "Communications" },
    { to: "/hq/provision",      icon: PlusCircle,       label: "Onboard residence" },
    { to: "/hq/activity",       icon: Activity,         label: "Activity" },
  ];

  const sidebarContent = (showMobileClose: boolean) => (
    <>
      <div className="px-6 pt-6 pb-4 border-b border-border flex items-center justify-between">
        <div>
          <Logo size="md" />
          <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-gold">HQ</div>
        </div>
        {showMobileClose && <MobileNavCloseButton onClick={closeSidebar} />}
      </div>

      <nav className="flex-1 py-4 px-3 space-y-0.5">
        {navItems.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            onClick={closeSidebar}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-gold-dim text-gold2 font-semibold"
                  : "text-muted hover:text-text hover:bg-surface2",
              )
            }
          >
            <i.icon className="h-4 w-4" />
            {i.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border px-3 py-3 space-y-1">
        <NavLink
          to="/help"
          onClick={closeSidebar}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors",
              isActive
                ? "bg-gold-dim text-gold2"
                : "text-muted hover:text-text hover:bg-surface2",
            )
          }
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </NavLink>
        <button
          onClick={async () => {
            await signOut();
            navigate("/login", { replace: true });
          }}
          className="w-full flex items-center gap-3 h-9 px-3 rounded-md text-sm text-muted hover:text-danger hover:bg-surface2"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-black">
      <SkipLink />

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:static w-[228px] flex-shrink-0 border-r border-border bg-surface flex-col"
        aria-label="HQ navigation"
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile off-canvas drawer */}
      <MobileNav
        id="hq-mobile-nav"
        open={sidebarOpen}
        onClose={closeSidebar}
        ariaLabel="HQ navigation"
      >
        {sidebarContent(true)}
      </MobileNav>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="border-b border-border bg-surface flex items-center justify-between px-4 md:px-[30px]"
          style={{ paddingTop: "env(safe-area-inset-top)", minHeight: "calc(56px + env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden flex items-center justify-center h-11 w-11 rounded-md bg-surface2 border border-border text-text hover:border-gold hover:text-gold transition-colors mr-1"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
              aria-controls="hq-mobile-nav"
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gold" />
              <h1 className="font-display text-base font-semibold text-text">Nestd HQ</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AvatarPill name={profile?.full_name ?? null} size={32} />
            <div className="text-right hidden sm:block">
              <div className="text-xs font-semibold text-text">{profile?.full_name ?? "—"}</div>
              <div className="text-[10px] uppercase tracking-wider text-gold">Super Admin</div>
            </div>
          </div>
        </header>

        <main
          id="main"
          className="flex-1 overflow-y-auto p-4 md:p-[26px_30px]"
          aria-hidden={sidebarOpen ? "true" : undefined}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
